import { useEffect, useState } from "react";
import { Check, Quote } from "lucide-react";
import { useLang } from "../components/Layout";
import { Reveal, Label } from "../components/Reveal";
import { CALENDLY_URL } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BilanPage = () => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ firstNames: "", lastName: "", maidenName: "", birthDate: "", sex: "", email: "" });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    document.title = t.pageTitles.bilan;
    document.documentElement.lang = lang;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", t.pageDescs.bilan);
  }, [lang, t]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/bilan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_names: form.firstNames,
          last_name: form.lastName,
          maiden_name: form.maidenName || null,
          birth_date: form.birthDate,
          sex: form.sex,
          email: form.email,
          lang,
        }),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const f = t.bilan.form;
  const inputCls =
    "w-full border border-white/15 bg-brand-ink px-4 py-3 text-sm font-light text-white placeholder-white/30 outline-none transition-colors duration-200 focus:border-brand-gold";

  return (
    <>
      <section data-testid="bilan-hero" className="border-b border-white/10 pt-40 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal y={24}>
            <Label testId="bilan-label">{t.bilan.label}</Label>
            <h1 className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] text-white">
              {t.bilan.title}
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg font-light leading-relaxed text-white/60">
              {t.bilan.sub}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="bilan-main" className="bg-brand-surface/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="grid gap-16 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <Reveal>
                <Label testId="bilan-benefits-label">{t.bilan.benefitsLabel}</Label>
                <ul className="mt-8 space-y-4">
                  {t.bilan.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-light text-white/70">
                      <Check size={16} className="mt-0.5 shrink-0 text-brand-gold" /> {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.15}>
                <Label testId="bilan-testimonials-label" className="mt-16 block">{t.bilan.testimonialsLabel}</Label>
                <div className="mt-8 space-y-6">
                  {t.bilan.testimonials.map((tst, i) => (
                    <figure
                      key={i}
                      data-testid={`testimonial-${i}`}
                      className="border border-white/10 bg-brand-ink p-6"
                    >
                      <Quote size={16} className="text-brand-gold" />
                      <blockquote className="mt-4 text-sm font-light italic leading-relaxed text-white/70">
                        {tst.text}
                      </blockquote>
                      <figcaption className="mt-4 text-xs text-white/40">
                        <span className="text-brand-gold">{tst.name}</span> — {tst.role}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-3">
              <Reveal delay={0.1}>
                <div className="border border-white/10 bg-brand-ink p-8 md:p-12">
                  <h2 className="font-serif text-3xl font-light text-white">{f.title}</h2>
                  {status === "success" ? (
                    <div data-testid="bilan-success" className="mt-10 border border-brand-gold/40 bg-brand-gold/10 p-8">
                      <p className="text-base font-light leading-relaxed text-white/80">{f.success}</p>
                      <a
                        data-testid="bilan-calendly-button"
                        href={CALENDLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-8 inline-flex items-center justify-center gap-3 bg-brand-gold px-8 py-4 text-sm font-semibold uppercase tracking-wide text-brand-ink transition-all duration-300 hover:bg-brand-gold-hover hover:shadow-[0_8px_32px_rgba(201,169,110,0.35)] hover:-translate-y-0.5"
                      >
                        {f.calendlyButton}
                      </a>
                    </div>
                  ) : (
                    <form data-testid="bilan-form" onSubmit={submit} className="mt-10 grid gap-6 sm:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">{f.firstNames} *</label>
                        <input data-testid="bilan-input-firstnames" required value={form.firstNames} onChange={set("firstNames")} className={inputCls} />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">{f.lastName} *</label>
                        <input data-testid="bilan-input-lastname" required value={form.lastName} onChange={set("lastName")} className={inputCls} />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">{f.maidenName}</label>
                        <input data-testid="bilan-input-maidenname" value={form.maidenName} onChange={set("maidenName")} className={inputCls} />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">{f.birthDate} *</label>
                        <input data-testid="bilan-input-birthdate" required type="date" value={form.birthDate} onChange={set("birthDate")} className={`${inputCls} [color-scheme:dark]`} />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">{f.sex} *</label>
                        <select data-testid="bilan-input-sex" required value={form.sex} onChange={set("sex")} className={inputCls}>
                          <option value="" disabled>—</option>
                          <option value="female">{f.female}</option>
                          <option value="male">{f.male}</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-xs uppercase tracking-[0.15em] text-white/50">{f.email} *</label>
                        <input data-testid="bilan-input-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
                      </div>
                      {status === "error" && (
                        <p data-testid="bilan-error" className="sm:col-span-2 text-sm text-red-400">{f.error}</p>
                      )}
                      <div className="sm:col-span-2">
                        <button
                          data-testid="bilan-submit-button"
                          type="submit"
                          disabled={status === "sending"}
                          className="w-full bg-brand-gold px-8 py-4 text-sm font-semibold uppercase tracking-wide text-brand-ink transition-all duration-300 hover:bg-brand-gold-hover disabled:opacity-60"
                        >
                          {status === "sending" ? f.sending : f.submit}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="bilan-quote-section" className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24">
          <Reveal className="text-center">
            <blockquote className="mx-auto max-w-3xl font-serif text-2xl md:text-3xl font-light italic leading-relaxed text-white/75">
              “{t.bilan.quote.text}”
            </blockquote>
            <p className="mt-6 uppercase tracking-luxe text-[11px] text-brand-gold">{t.bilan.quote.author}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default BilanPage;
